import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useBalance } from 'wagmi';
import { faCheckCircle } from '@soundxyz/font-awesome/pro-solid-svg-icons/index.mjs';
import { LoadingSpinner } from '@components/basics/LoadingSpinner';
import { useAuth } from '@context/auth/base';
import { useConnectedAuth } from '@context/auth/connected';
import { useToast } from '@context/toast';
import { useConnectedWallet } from '@hooks/useConnectedWallet';
import { AVAILABLE_CHAIN_IDS_LIST } from '@utils/constants';
import { formatWeiToEthForDisplay, humanReadableChainName } from '@utils/helpers';

const Balance = ({ isActiveChain, chainId }: { isActiveChain: boolean; chainId: number }) => {
  const { authUserAddress } = useAuth();
  const { isSwitchingNetwork, switchNetworkChain } = useConnectedAuth();

  const { openToast } = useToast();
  const {
    data: balanceData,
    isError: balanceError,
    isLoading: balanceLoading,
  } = useBalance({
    address: authUserAddress || '0x000',
    enabled: !!authUserAddress,
    formatUnits: 'gwei',
    chainId,
  });

  const handleClick = useConnectedWallet({
    onAuth() {
      if (isActiveChain) return;

      switchNetworkChain(chainId)?.catch(() => {
        openToast({
          text: 'Network switch failed.',
          variant: 'error',
        });
      });
    },
  });

  return (
    <>
      {balanceLoading || balanceError ? (
        <div className="flex h-[42px] w-full items-center justify-center">
          <LoadingSpinner className="aspect-square w-3" />
        </div>
      ) : (
        <button
          className="flex w-full cursor-pointer items-center justify-between px-3 py-3 md:py-2.5 md:hover:bg-base50"
          onClick={handleClick}
        >
          <div className="flex items-center font-base text-base-m">
            <h3 className=" text-base900">{humanReadableChainName(chainId)}</h3>
            <span className="text-base400">&nbsp;•&nbsp;</span>
            <p className="text-base-m text-base400">
              {balanceData ? (
                <>
                  {formatWeiToEthForDisplay({
                    weiValue: balanceData.value.toString(),
                    maxDecimals: 4,
                  })}{' '}
                  ETH
                </>
              ) : (
                <>Unable to load</>
              )}
            </p>
          </div>
          {isSwitchingNetwork && !isActiveChain && <LoadingSpinner className="h-4 w-4" />}
          {isActiveChain && <FontAwesomeIcon icon={faCheckCircle} className="text-base-l" />}
        </button>
      )}
    </>
  );
};

export const Balances = () => {
  const {
    activeChainIdWithFallback: { current: activeChainId },
  } = useConnectedAuth();

  return (
    <div className="flex w-[92vw] flex-col md:w-full md:min-w-[220px]">
      {AVAILABLE_CHAIN_IDS_LIST.map(chainId => {
        return (
          <Balance isActiveChain={activeChainId === chainId} chainId={chainId} key={chainId} />
        );
      })}
    </div>
  );
};
